import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    fbq: (action: string, event: string) => void;
  }
}

export default function BenefitsSection() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    return () => {
      if (headingRef.current) {
        observer.unobserve(headingRef.current);
      }
    };
  }, []);

  const benefits = [
    {
      icon: 'ri-gift-line',
      title: '入会金無料',
      subtitle: '¥5,500 → ¥0',
      description: '体験レッスン当日にご入会いただくと、入会金5,500円が無料になります。'
    },
    {
      icon: 'ri-calendar-check-line',
      title: '初月レッスン料半額',
      subtitle: '初月50%OFF',
      description: '入会月のレッスン料が半額になります。お得にスタートできるチャンスです。'
    },
    {
      icon: 'ri-movie-line',
      title: '歌ってみた動画プレゼント',
      subtitle: '体験レッスンの音源を使用',
      description: '体験レッスンで録音した音源を使って、講師が「歌ってみた動画」を制作してプレゼント。完成イメージが掴めます。'
    }
  ];

  const handleTrialClick = () => {
    if (window.fbq) {
      window.fbq('track', 'Lead');
    }
    const formSection = document.getElementById('trial-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs sm:text-sm font-bold px-4 sm:px-6 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
            期間限定
          </div>
          <h2 
            ref={headingRef}
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            今だけの入会特典
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            今なら、お得に始められる3つの特典をご用意
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-orange-100">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-500 rounded-full mb-4 sm:mb-6 mx-auto">
                <i className={`${benefit.icon} text-2xl sm:text-3xl text-white`}></i>
              </div>
              <div className="text-center mb-3 sm:mb-4">
                <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold px-3 sm:px-4 py-1 rounded-full mb-2 sm:mb-3">
                  特典 {index + 1}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-orange-500 font-bold text-base sm:text-lg mb-3 sm:mb-4">
                  {benefit.subtitle}
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed text-center text-sm sm:text-base">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={handleTrialClick}
            className="whitespace-nowrap bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-base sm:text-lg md:text-xl px-8 sm:px-10 md:px-12 py-4 sm:py-5 md:py-6 rounded-lg shadow-xl transition-all duration-300 cursor-pointer inline-flex items-center gap-2 sm:gap-3"
          >
            特典付き無料体験を予約する
            <i className="ri-arrow-right-s-line text-xl sm:text-2xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}